import {
  Button,
  DateInput,
  Modal,
  Select,
  SelectOptionType,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { toDateOnlyPayload } from '../../../../../../utils/dates';
import {
  CollectionFeeMode,
  CollectionFeeRuleItem,
  createCollectionFeeRule,
  updateCollectionFeeRule,
} from '../api';

const dictPrefix = 'financialPortal.settings.collectionFees';

const MODES: CollectionFeeMode[] = ['capitalPercentage', 'fixedAmount'];

interface FormValues {
  mode: SelectOptionType | null;
  percentage: string;
  amount: string;
  overdueDays: string;
  validFrom: Date | string | null;
}

interface Props {
  isOpen: boolean;
  item: CollectionFeeRuleItem | null;
  onClose: () => void;
  onSaved: () => void;
}

// Ver la nota en LateInterest/components/ConfigModal.tsx sobre `any` y el trim.
function validateRange(
  min: number,
  max: number,
  message: string,
  integer: boolean,
) {
  return (raw: any) => {
    const trimmed = typeof raw === 'string' ? raw.trim() : raw;
    if (trimmed === '' || trimmed === null || trimmed === undefined) {
      return message;
    }
    const value = Number(trimmed);
    if (Number.isNaN(value) || value < min || value > max) {
      return message;
    }
    if (integer && !Number.isInteger(value)) {
      return message;
    }
    return true;
  };
}

export function ConfigModal({ isOpen, item, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { control, handleSubmit, formState, reset } = useForm<FormValues>({
    defaultValues: {
      mode: null,
      percentage: '',
      amount: '',
      overdueDays: '',
      validFrom: null,
    },
  });

  const modeOptions = useMemo<SelectOptionType[]>(
    () =>
      MODES.map((mode) => ({
        value: mode,
        label: t(`${dictPrefix}.modes.${mode}`),
      })),
    [t],
  );

  useEffect(() => {
    if (item) {
      reset({
        mode: modeOptions.find((option) => option.value === item.mode) ?? null,
        percentage: item.percentage === null ? '' : String(item.percentage),
        amount: item.amount === null ? '' : String(item.amount),
        overdueDays: String(item.overdueDays),
        validFrom: item.validFrom,
      });
    } else {
      reset({
        mode: null,
        percentage: '',
        amount: '',
        overdueDays: '',
        validFrom: null,
      });
    }
  }, [isOpen, item, modeOptions, reset]);

  const values = useWatch({ control });
  const mode = values.mode?.value as CollectionFeeMode | undefined;

  const onSubmit = useCallback(
    async (formValues: FormValues) => {
      if (!formValues.mode) {
        return;
      }
      const selected = formValues.mode.value as CollectionFeeMode;
      // Sólo se manda el campo del modo elegido; el servidor descarta el otro
      // de todos modos, pero mandarlo confundiría al que lea el tráfico.
      const payload = {
        mode: selected,
        percentage:
          selected === 'capitalPercentage'
            ? Number(String(formValues.percentage).trim())
            : undefined,
        amount:
          selected === 'fixedAmount'
            ? Number(String(formValues.amount).trim())
            : undefined,
        overdueDays: Number(String(formValues.overdueDays).trim()),
        validFrom: toDateOnlyPayload(formValues.validFrom),
      };

      const { error } = item
        ? await updateCollectionFeeRule(item.id, payload)
        : await createCollectionFeeRule(payload);

      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.saveError`),
        });
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${dictPrefix}.saveSuccess`),
      });
      onSaved();
    },
    [item, onSaved, t],
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md">
      <h1 className="text-dark fs-22 mb-4 text-center">
        {t(`${dictPrefix}.form.title`)}
      </h1>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Select
          name="mode"
          label={t(`${dictPrefix}.form.mode`)}
          placeholder={t(`${dictPrefix}.form.modePlaceholder`)}
          control={control}
          options={modeOptions}
          rules={{ required: t(`${dictPrefix}.form.required`) as string }}
          isClearable={false}
        />
        {mode === 'capitalPercentage' && (
          <TextInput
            name="percentage"
            label={t(`${dictPrefix}.form.percentage`)}
            control={control}
            rules={{
              required: t(`${dictPrefix}.form.required`) as string,
              validate: validateRange(
                0.000001,
                100,
                t(`${dictPrefix}.form.percentageRange`) as string,
                false,
              ),
            }}
          />
        )}
        {mode === 'fixedAmount' && (
          <TextInput
            name="amount"
            label={t(`${dictPrefix}.form.amount`)}
            control={control}
            rules={{
              required: t(`${dictPrefix}.form.required`) as string,
              validate: validateRange(
                0.01,
                Number.MAX_SAFE_INTEGER,
                t(`${dictPrefix}.form.amountRange`) as string,
                false,
              ),
            }}
          />
        )}
        <TextInput
          name="overdueDays"
          label={t(`${dictPrefix}.form.overdueDays`)}
          control={control}
          rules={{
            required: t(`${dictPrefix}.form.required`) as string,
            validate: validateRange(
              1,
              365,
              t(`${dictPrefix}.form.overdueDaysRange`) as string,
              true,
            ),
          }}
        />
        <DateInput
          name="validFrom"
          label={t(`${dictPrefix}.form.validFrom`)}
          control={control}
          minDate={new Date()}
          rules={{ required: t(`${dictPrefix}.form.required`) as string }}
        />
        <Row className="mt-4">
          <Col xs={12} md={6}>
            <Button
              outlined
              fullwidth
              text={t(`${dictPrefix}.form.cancel`)}
              onClick={onClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              type="submit"
              fullwidth
              loading={formState.isSubmitting}
              text={t(`${dictPrefix}.form.save`)}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
